import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import Layout from '../../components/Layout';
import { useTranslation } from '../../i18n/useTranslation';
import { useAbpPermissions } from '../../contexts/AbpPermissionsContext';
import { AbpPermissions } from '../../constants/permissions';
import { getActiveBooksellers, type BooksellerDto } from '../../api/booksellersService';
import {
  missingBooksService,
  DEFAULT_MISSING_BOOK_FILTER,
  type MissingBookFilter,
  type MissingBookWithNavigationPropertiesDto,
} from '../../api/missingBooksService';

// Client-side filter for resolved rows — the Suite list endpoint doesn't have a
// "only where ResolvedAt IS NULL" toggle (both min and max map to range filters),
// and writing an extra API call for this felt heavier than the UX needed.
function applyResolvedFilter(
  rows: MissingBookWithNavigationPropertiesDto[],
  onlyUnresolved: boolean
) {
  if (!onlyUnresolved) return rows;
  return rows.filter((r) => !r.missingBook.resolvedAt);
}

export default function ManageMissingBooksPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAbpPermissions();
  const canResolve = hasPermission(AbpPermissions.ResolveMissingBooks);

  const [filters, setFilters] = useState<MissingBookFilter>(DEFAULT_MISSING_BOOK_FILTER);
  const [booksellers, setBooksellers] = useState<BooksellerDto[]>([]);
  const [downloadState, setDownloadState] = useState<'idle' | 'downloading' | 'error'>('idle');

  useEffect(() => {
    getActiveBooksellers()
      .then(setBooksellers)
      .catch(() => setBooksellers([]));
  }, []);

  const query = useQuery({
    queryKey: ['missing-books', filters],
    queryFn: () => missingBooksService.list(filters),
    staleTime: 15_000,
  });

  const queryClient = useQueryClient();
  const resolveMutation = useMutation({
    mutationFn: (id: string) => missingBooksService.resolve(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['missing-books'] }),
  });

  const rows = useMemo(
    () => applyResolvedFilter(query.data?.items ?? [], filters.onlyUnresolved ?? false),
    [query.data, filters.onlyUnresolved]
  );

  const handleDownload = async () => {
    setDownloadState('downloading');
    try {
      const blob = await missingBooksService.exportCsv(filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `missing-books-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setDownloadState('idle');
    } catch (e) {
      console.error('CSV export failed', e);
      setDownloadState('error');
    }
  };

  const updateFilters = (patch: Partial<MissingBookFilter>) => {
    setFilters((prev) => ({ ...prev, ...patch, page: 1 }));
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
          <div>
            <h1 className="text-2xl font-semibold">{t('App.Admin.MissingBooks.ManageMissingBooksPage.Title')}</h1>
            <p className="text-sm text-neutral-500 mt-1">
              {t('App.Admin.MissingBooks.ManageMissingBooksPage.Subtitle')}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
              className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-neutral-300 rounded text-sm hover:bg-neutral-50 disabled:opacity-50 min-h-[44px]"
              aria-label={t('App.Admin.MissingBooks.ManageMissingBooksPage.Refresh')}
            >
              <RefreshCw className={`w-4 h-4 ${query.isFetching ? 'animate-spin' : ''}`} />
              {t('App.Admin.MissingBooks.ManageMissingBooksPage.Refresh')}
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloadState === 'downloading'}
              className="inline-flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded text-sm hover:bg-primary-700 disabled:opacity-50 min-h-[44px]"
            >
              <Download className="w-4 h-4" />
              {downloadState === 'downloading' ? t('App.Admin.MissingBooks.ManageMissingBooksPage.Preparing') : t('App.Admin.MissingBooks.ManageMissingBooksPage.DownloadCSV')}
            </button>
          </div>
        </div>

        {downloadState === 'error' && (
          <div className="mb-4 p-3 bg-error-50 border border-error-200 rounded text-sm text-error-800 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {t('App.Admin.MissingBooks.ManageMissingBooksPage.CsvExportError')}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-neutral-200 rounded p-4 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-neutral-600">{t('App.Admin.MissingBooks.ManageMissingBooksPage.SearchLabel')}</span>
              <input
                type="text"
                value={filters.q ?? ''}
                onChange={(e) => updateFilters({ q: e.target.value || undefined })}
                placeholder={t('App.Admin.MissingBooks.ManageMissingBooksPage.SearchPlaceholder')}
                className="mt-1 w-full border border-neutral-300 rounded px-2 py-1.5 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-neutral-600">{t('App.Admin.MissingBooks.ManageMissingBooksPage.BooksellerLabel')}</span>
              <select
                value={filters.booksellerId ?? ''}
                onChange={(e) => updateFilters({ booksellerId: e.target.value || undefined })}
                className="mt-1 w-full border border-neutral-300 rounded px-2 py-1.5 text-sm bg-white"
              >
                <option value="">{t('App.Admin.MissingBooks.ManageMissingBooksPage.AllBooksellers')}</option>
                {booksellers.map((b) => (
                  <option key={b.id} value={b.id}>{b.displayName || b.name}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-neutral-600">{t('App.Admin.MissingBooks.ManageMissingBooksPage.SortLabel')}</span>
              <select
                value={`${filters.sort ?? 'requestCount'}:${filters.dir ?? 'desc'}`}
                onChange={(e) => {
                  const [sort, dir] = e.target.value.split(':');
                  updateFilters({ sort: sort as MissingBookFilter['sort'], dir: dir as 'asc' | 'desc' });
                }}
                className="mt-1 w-full border border-neutral-300 rounded px-2 py-1.5 text-sm bg-white"
              >
                <option value="requestCount:desc">{t('App.Admin.MissingBooks.ManageMissingBooksPage.SortMostRequested')}</option>
                <option value="lastSeenAt:desc">{t('App.Admin.MissingBooks.ManageMissingBooksPage.SortRecentlyRequested')}</option>
                <option value="firstSeenAt:asc">{t('App.Admin.MissingBooks.ManageMissingBooksPage.SortFirstRequested')}</option>
                <option value="title:asc">{t('App.Admin.MissingBooks.ManageMissingBooksPage.SortTitleAZ')}</option>
              </select>
            </label>
            <label className="flex items-end gap-2">
              <input
                type="checkbox"
                checked={filters.onlyUnresolved ?? false}
                onChange={(e) => updateFilters({ onlyUnresolved: e.target.checked })}
                className="h-4 w-4"
              />
              <span className="text-sm text-neutral-700">{t('App.Admin.MissingBooks.ManageMissingBooksPage.HideResolved')}</span>
            </label>
          </div>
        </div>

        {/* Table */}
        {query.isLoading && <p>{t('App.Admin.MissingBooks.ManageMissingBooksPage.Loading')}</p>}
        {query.error && (
          <p className="text-red-600">
            {t('App.Admin.MissingBooks.ManageMissingBooksPage.LoadError', { error: String((query.error as Error)?.message ?? query.error) })}
          </p>
        )}

        {query.data && (
          <div className="bg-white border border-neutral-200 rounded overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                <tr>
                  <th className="text-left px-3 py-2">{t('App.Admin.MissingBooks.ManageMissingBooksPage.ColBooksellerBookId')}</th>
                  <th className="text-left px-3 py-2">{t('App.Admin.MissingBooks.ManageMissingBooksPage.ColISBN')}</th>
                  <th className="text-left px-3 py-2">{t('App.Admin.MissingBooks.ManageMissingBooksPage.ColTitle')}</th>
                  <th className="text-left px-3 py-2">{t('App.Admin.MissingBooks.ManageMissingBooksPage.ColBookseller')}</th>
                  <th className="text-right px-3 py-2">{t('App.Admin.MissingBooks.ManageMissingBooksPage.ColRequests')}</th>
                  <th className="text-left px-3 py-2">{t('App.Admin.MissingBooks.ManageMissingBooksPage.ColLastSeen')}</th>
                  <th className="text-left px-3 py-2">{t('App.Admin.MissingBooks.ManageMissingBooksPage.ColStatus')}</th>
                  {canResolve && <th className="text-right px-3 py-2">{t('App.Admin.MissingBooks.ManageMissingBooksPage.ColActions')}</th>}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={canResolve ? 8 : 7} className="px-3 py-6 text-center text-neutral-500">
                      {t('App.Admin.MissingBooks.ManageMissingBooksPage.NoResults')}
                    </td>
                  </tr>
                )}
                {rows.map((row) => {
                  const mb = row.missingBook;
                  const isResolved = !!mb.resolvedAt;
                  return (
                    <tr key={mb.id} className="border-t border-neutral-100">
                      <td className="px-3 py-2 font-mono text-xs">{mb.booksellerBookId}</td>
                      <td className="px-3 py-2">{mb.isbn || <span className="text-neutral-400">—</span>}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium">{mb.title || <span className="text-neutral-400">{t('App.Admin.MissingBooks.ManageMissingBooksPage.UnknownTitle')}</span>}</div>
                        {mb.author && <div className="text-xs text-neutral-500">{mb.author}</div>}
                      </td>
                      <td className="px-3 py-2">{row.bookseller?.displayName || row.bookseller?.name}</td>
                      <td className="px-3 py-2 text-right font-semibold">{mb.requestCount}</td>
                      <td className="px-3 py-2 text-neutral-600 whitespace-nowrap">
                        {new Date(mb.lastSeenAt).toLocaleString()}
                      </td>
                      <td className="px-3 py-2">
                        {isResolved ? (
                          <span className="inline-flex items-center gap-1 text-success-700 text-xs">
                            <CheckCircle2 className="w-3.5 h-3.5" /> {t('App.Admin.MissingBooks.ManageMissingBooksPage.StatusResolved')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-700 text-xs">
                            <AlertCircle className="w-3.5 h-3.5" /> {t('App.Admin.MissingBooks.ManageMissingBooksPage.StatusOpen')}
                          </span>
                        )}
                      </td>
                      {canResolve && (
                        <td className="px-3 py-2 text-right">
                          {!isResolved && (
                            <button
                              type="button"
                              onClick={() => resolveMutation.mutate(mb.id)}
                              disabled={resolveMutation.isPending}
                              className="text-xs text-primary-600 hover:text-primary-700 disabled:opacity-50"
                            >
                              {t('App.Admin.MissingBooks.ManageMissingBooksPage.MarkResolved')}
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="flex items-center justify-between p-3 border-t border-neutral-100 text-sm">
              <span className="text-neutral-500">
                {t('App.Admin.MissingBooks.ManageMissingBooksPage.TotalCount', { count: query.data.totalCount.toLocaleString() })}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={filters.page <= 1}
                  onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
                  className="px-2 py-1 border border-neutral-300 rounded disabled:opacity-40"
                >
                  {t('App.Admin.MissingBooks.ManageMissingBooksPage.PrevButton')}
                </button>
                <span>{t('App.Admin.MissingBooks.ManageMissingBooksPage.Page', { page: filters.page })}</span>
                <button
                  type="button"
                  disabled={filters.page * filters.pageSize >= query.data.totalCount}
                  onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
                  className="px-2 py-1 border border-neutral-300 rounded disabled:opacity-40"
                >
                  {t('App.Admin.MissingBooks.ManageMissingBooksPage.NextButton')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
